import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table } from "antd";
import { useIntl } from "react-intl";

import { ExpandedInnerTableWrapper } from "../styles";
import { fetchPackageInternalDocuments } from "../../../service/uploadFlow.service";

const EXPAND_ICON_COLUMN_WIDTH = 48;
const SPACER_COLUMN_KEY = "__expand_spacer__";
const ACTION_COLUMN_KEYS = ["actions", "action"];
const DEFAULT_STATUS = "PENDING";

const toRowShape = (doc = {}) => ({
  ...doc,
  id: doc.documentConfigId,
  idExecution: doc.idExecution ?? doc.documentExecutionId ?? null,
  fileName: doc.fileName ?? "",
  status: doc.approvalStatus ?? doc.status ?? DEFAULT_STATUS,
  url: doc.urlForDownload ?? doc.url ?? null,
});

const ExpandedDocumentRow = ({
  columns,
  rowKey = "id",
  eventId,
  uploadFlowId,
  executionId,
  token,
  queryParams,
}) => {
  const intl = useIntl();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const serializedQueryParams = useMemo(
    () =>
      JSON.stringify({
        ...(queryParams ?? {}),
        ...(executionId && { executionId }),
      }),
    [queryParams, executionId]
  );

  // Na linha expandida, a coluna de ações fica vazia.
  // O envio do pacote é feito pelo botão "Enviar pacote" da linha pai,
  // que dispara o envio em lote com todos os documentos internos.
  const overriddenColumns = useMemo(
    () =>
      columns.map((col) => {
        const isActionColumn =
          ACTION_COLUMN_KEYS.includes(col.key) ||
          ACTION_COLUMN_KEYS.includes(col.dataIndex);

        if (!isActionColumn) return col;

        return { ...col, render: () => null };
      }),
    [columns]
  );

  const alignedColumns = useMemo(
    () => [
      {
        key: SPACER_COLUMN_KEY,
        width: EXPAND_ICON_COLUMN_WIDTH,
        render: () => null,
      },
      ...overriddenColumns,
    ],
    [overriddenColumns]
  );

  const loadInternalDocuments = useCallback(async () => {
    if (!eventId || !uploadFlowId) {
      setRows([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = JSON.parse(serializedQueryParams);
      const data = await fetchPackageInternalDocuments({
        eventId,
        uploadFlowId,
        token,
        params,
      });

      const list = Array.isArray(data) ? data : data?.documents ?? [];
      setRows(list.map(toRowShape));
    } catch (err) {
      setError(intl.formatMessage({ id: "expanded_documents.fetch_error" }));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [eventId, uploadFlowId, token, serializedQueryParams, intl]);

  useEffect(() => {
    loadInternalDocuments();
  }, [loadInternalDocuments]);

  return (
    <ExpandedInnerTableWrapper>
      <Table
        columns={alignedColumns}
        dataSource={rows}
        rowKey={rowKey}
        pagination={false}
        showHeader={false}
        size="small"
        tableLayout="fixed"
        loading={loading}
        locale={{
          emptyText: error
            ? error
            : intl.formatMessage({ id: "expanded_documents.empty" }),
        }}
      />
    </ExpandedInnerTableWrapper>
  );
};

export default ExpandedDocumentRow;
